import { useState, useEffect } from 'react';
import api from '../../../api/client';

const POLICY_TYPES    = ['domain', 'business_process', 'report'];
const PRINCIPAL_TYPES = ['role', 'user', 'group'];

const EMPTY_POLICY = { name: '', domain: '', type: 'domain' };
const EMPTY_GRANT  = { principalType: 'role', principalRef: '', actions: '', constraintConfig: '' };

export default function PoliciesPage() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Create-policy modal
  const [addOpen,     setAddOpen]     = useState(false);
  const [policyForm,  setPolicyForm]  = useState(EMPTY_POLICY);
  const [savingPol,   setSavingPol]   = useState(false);

  // Expanded detail
  const [selectedId,  setSelectedId]  = useState(null);
  const [detail,      setDetail]      = useState(null);
  const [detailLoad,  setDetailLoad]  = useState(false);

  // Add-grant modal
  const [grantOpen,   setGrantOpen]   = useState(false);
  const [grantForm,   setGrantForm]   = useState(EMPTY_GRANT);
  const [savingGrant, setSavingGrant] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/security-policies');
      setItems(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function loadDetail(policyId) {
    setDetailLoad(true);
    try {
      const { data } = await api.get(`/security-policies/${policyId}`);
      setDetail(data.data?.policy || null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setDetailLoad(false);
    }
  }

  function toggleRow(policyId) {
    if (selectedId === policyId) {
      setSelectedId(null);
      setDetail(null);
    } else {
      setSelectedId(policyId);
      setDetail(null);
      loadDetail(policyId);
    }
  }

  // --- Create policy ---
  function patchPolicy(key, val) { setPolicyForm((f) => ({ ...f, [key]: val })); }

  async function handleCreatePolicy(e) {
    e.preventDefault();
    setSavingPol(true);
    setError(null);
    try {
      await api.post('/security-policies', {
        name:   policyForm.name,
        domain: policyForm.domain,
        type:   policyForm.type,
      });
      setPolicyForm(EMPTY_POLICY);
      setAddOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSavingPol(false);
    }
  }

  // --- Add grant ---
  function patchGrant(key, val) { setGrantForm((f) => ({ ...f, [key]: val })); }

  async function handleAddGrant(e) {
    e.preventDefault();
    setSavingGrant(true);
    setError(null);
    try {
      const actions = grantForm.actions.split(',').map((s) => s.trim()).filter(Boolean);
      const body = {
        principalType: grantForm.principalType,
        principalRef:  grantForm.principalRef,
        access:        { actions },
      };
      if (grantForm.constraintConfig.trim()) {
        body.constraintConfig = JSON.parse(grantForm.constraintConfig);
      }
      await api.post(`/security-policies/${selectedId}/grants`, body);
      setGrantForm(EMPTY_GRANT);
      setGrantOpen(false);
      await loadDetail(selectedId);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSavingGrant(false);
    }
  }

  return (
    <div data-testid="security-policies-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Security Policies</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginBottom: '1rem' }}>
        <button data-testid="secpol-add" onClick={() => setAddOpen(true)}>
          Create Policy
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Domain</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr
                key={p.id}
                data-testid={`secpol-row-${p.id}`}
                style={{ cursor: 'pointer', background: selectedId === p.id ? '#eef' : 'transparent' }}
                onClick={() => toggleRow(p.id)}
              >
                <td>{p.name}</td>
                <td>{p.domain}</td>
                <td>{p.type}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={3}>No security policies yet.</td></tr>}
          </tbody>
        </table>
      )}

      {/* Expanded detail */}
      {selectedId && (
        <div
          data-testid={`secpol-detail-${selectedId}`}
          style={{ marginTop: '1rem', border: '1px solid #ccc', borderRadius: 8, padding: '1rem' }}
        >
          {detailLoad ? (
            <p>Loading policy...</p>
          ) : !detail ? (
            <p>Policy not found.</p>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>{detail.name}</h3>
                <button data-testid="secpol-grant-add" onClick={() => { setGrantForm(EMPTY_GRANT); setGrantOpen(true); }}>
                  Add Grant
                </button>
              </div>

              <h4 style={{ marginBottom: '0.25rem' }}>Grants</h4>
              <table style={{ width: '100%' }}>
                <thead>
                  <tr><th>Principal</th><th>Ref</th><th>Actions</th><th>Constraint</th></tr>
                </thead>
                <tbody>
                  {(detail.grants || []).map((g) => {
                    const access = typeof g.access === 'string' ? safeParse(g.access) : g.access;
                    const cc = typeof g.constraint_config === 'string' ? safeParse(g.constraint_config) : g.constraint_config;
                    return (
                      <tr key={g.id} data-testid={`secpol-grant-row-${g.id}`}>
                        <td>{g.principal_type}</td>
                        <td>{g.principal_ref}</td>
                        <td>{access?.actions ? access.actions.join(', ') : '—'}</td>
                        <td>{cc ? <code>{JSON.stringify(cc)}</code> : '—'}</td>
                      </tr>
                    );
                  })}
                  {!(detail.grants || []).length && <tr><td colSpan={4}>No grants.</td></tr>}
                </tbody>
              </table>

              <h4 style={{ marginBottom: '0.25rem', marginTop: '1rem' }}>Constraints</h4>
              <table style={{ width: '100%' }}>
                <thead>
                  <tr><th>Kind</th><th>Config</th></tr>
                </thead>
                <tbody>
                  {(detail.constraints || []).map((c) => {
                    const cfg = typeof c.config === 'string' ? safeParse(c.config) : c.config;
                    return (
                      <tr key={c.id} data-testid={`secpol-constraint-row-${c.id}`}>
                        <td>{c.kind}</td>
                        <td>{cfg ? <code>{JSON.stringify(cfg)}</code> : '—'}</td>
                      </tr>
                    );
                  })}
                  {!(detail.constraints || []).length && <tr><td colSpan={2}>No constraints.</td></tr>}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Create-policy modal */}
      {addOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 380 }}>
            <h3>Create Security Policy</h3>
            <form onSubmit={handleCreatePolicy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  placeholder="Name"
                  value={policyForm.name}
                  onChange={(e) => patchPolicy('name', e.target.value)}
                  required
                />
                <input
                  placeholder="Domain (e.g. expenses)"
                  value={policyForm.domain}
                  onChange={(e) => patchPolicy('domain', e.target.value)}
                  required
                />
                <label>
                  Type
                  <select value={policyForm.type} onChange={(e) => patchPolicy('type', e.target.value)}>
                    {POLICY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="secpol-form-submit" disabled={savingPol}>
                  {savingPol ? 'Creating...' : 'Create'}
                </button>
                <button type="button" onClick={() => { setAddOpen(false); setError(null); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add-grant modal */}
      {grantOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 400 }}>
            <h3>Add Grant</h3>
            <form onSubmit={handleAddGrant}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label>
                  Principal Type
                  <select value={grantForm.principalType} onChange={(e) => patchGrant('principalType', e.target.value)}>
                    {PRINCIPAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <input
                  placeholder="Principal Ref (role/user/group id)"
                  value={grantForm.principalRef}
                  onChange={(e) => patchGrant('principalRef', e.target.value)}
                  required
                />
                <input
                  placeholder="Actions (comma-separated, e.g. read, export)"
                  value={grantForm.actions}
                  onChange={(e) => patchGrant('actions', e.target.value)}
                  required
                />
                <textarea
                  placeholder='Constraint Config JSON (optional, e.g. {"entity_id":"123"})'
                  value={grantForm.constraintConfig}
                  onChange={(e) => patchGrant('constraintConfig', e.target.value)}
                  rows={3}
                />
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="secpol-grant-form-submit" disabled={savingGrant}>
                  {savingGrant ? 'Saving...' : 'Add Grant'}
                </button>
                <button type="button" onClick={() => { setGrantOpen(false); setError(null); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}
